/**
 * The few words the usage popover, the limit error and the home greeting
 * derive from a clock. Pure, with `now` passed in, so they are tested rather
 * than eyeballed at three different times of day.
 */

/**
 * When a limit window frees up: "in 3 hr 3 min" inside a day, "Wed 05:00"
 * beyond — the prototype's two forms. Null when nothing has been used, so
 * there is nothing to reset.
 */
export function resetWhen(resetsAt: number | null, now: number = Date.now() / 1000): string | null {
  if (resetsAt === null) return null;
  const left = Math.max(0, resetsAt - now);

  if (left < 24 * 3600) {
    const total = Math.max(1, Math.ceil(left / 60));
    const hours = Math.floor(total / 60);
    const minutes = total % 60;
    if (!hours) return `in ${minutes} min`;
    return minutes ? `in ${hours} hr ${minutes} min` : `in ${hours} hr`;
  }

  const at = new Date(resetsAt * 1000);
  const day = at.toLocaleDateString('en-GB', { weekday: 'short' });
  const time = `${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}`;
  return `${day} ${time}`;
}

/** "Resets in 3 hr 3 min", or nothing at all. */
export function resetLine(resetsAt: number | null, now?: number): string {
  const when = resetWhen(resetsAt, now);
  return when ? `Resets ${when}` : '';
}

export function greeting(hour: number): 'Morning' | 'Afternoon' | 'Evening' {
  if (hour >= 5 && hour < 12) return 'Morning';
  if (hour >= 12 && hour < 18) return 'Afternoon';
  return 'Evening';
}

export const clampPct = (pct: number): number => Math.max(0, Math.min(100, Math.round(pct)));

/** "0%" only when nothing was used; a sliver the rounding hides is "<1%". */
export const pctLabel = (pct: number, used: number): string =>
  used > 0 && clampPct(pct) === 0 ? '<1%' : `${clampPct(pct)}%`;

/** A usage window's figure: its percentage, or "No limit" where there is none. */
export const windowLabel = (w: { pct: number; used: number; allowance: number | null }): string =>
  w.allowance === null ? 'No limit' : pctLabel(w.pct, w.used);

/** Pence as pounds: "£3.20", and "<£0.01" for a sliver. */
export function poundsLabel(pence: number): string {
  if (pence > 0 && pence < 1) return '<£0.01';
  return `£${(pence / 100).toFixed(2)}`;
}
